import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { HomeReportDrawer } from "../components/HomeReportDrawer";
import { api } from "../lib/api";
import { logError } from "../lib/errors";
import { getUnseenHomeReportFingerprints, markHomeReportsSeen } from "../lib/homeReports";
import { KNOWLEDGE_BACKGROUND_EVENT, readKnowledgeHomeActivityLogs } from "../lib/knowledgeBackground";
import {
  addPendingExecutiveMemoReport,
  groupAgentJobActivityLogsByDay,
  mergeHomeActivityLogsByRecency,
  type HomeActivityLog,
} from "../lib/reportActivity";
import { storage } from "../lib/storage";

const ACTIVE_JOB_REFRESH_INTERVAL_MS = 15000;
const PASSIVE_REFRESH_MIN_INTERVAL_MS = 1000;

const REPORT_DISABLED_PATHS = [
  "/",
  "/landing",
  "/splash",
  "/sales-support",
  "/president-consult",
  "/knowledge-use",
  "/login",
  "/register",
  "/onboarding",
  "/email-otp/email",
  "/email-otp/verify",
  "/line",
  "/line-login",
  "/line-register",
  "/line-register/callback",
  "/line-register/complete",
  "/google-link",
  "/google-link/complete",
  "/line-link",
  "/line-link/complete",
  "/terms",
  "/privacy",
  "/commercial-transactions",
];

export function isReportCenterEnabledPath(pathname: string): boolean {
  return !REPORT_DISABLED_PATHS.includes(pathname);
}

type RefreshOptions = { force?: boolean };

type AgentReportState = {
  userId: string | null;
  todayLogs: HomeActivityLog[];
  yesterdayLogs: HomeActivityLog[];
  hasActiveJobs: boolean;
};

const EMPTY_AGENT_REPORT_STATE: AgentReportState = {
  userId: null,
  todayLogs: [],
  yesterdayLogs: [],
  hasActiveJobs: false,
};

type ReportCenterValue = {
  isEnabled: boolean;
  todayLogs: HomeActivityLog[];
  yesterdayLogs: HomeActivityLog[];
  unseenCount: number;
  hasActiveJobs: boolean;
  isOpen: boolean;
  openReports: () => void;
  closeReports: () => void;
  refreshReports: (options?: RefreshOptions) => Promise<void>;
  markViewedReports: (logs: HomeActivityLog[]) => void;
  setCharacterLastName: (name: string | null | undefined) => void;
};

const ReportCenterContext = createContext<ReportCenterValue | null>(null);

export function ReportCenterProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const auth = storage.getAuth();
  const userId = auth.userId?.trim() || null;
  const authenticated = Boolean(userId && (auth.accessToken || auth.refreshToken));
  const enabled = authenticated && isReportCenterEnabledPath(location.pathname);
  const [agentReports, setAgentReports] = useState<AgentReportState>(EMPTY_AGENT_REPORT_STATE);
  const [isOpen, setIsOpen] = useState(false);
  const [drawerNewReportFingerprints, setDrawerNewReportFingerprints] = useState<string[]>([]);
  const [seenRevision, setSeenRevision] = useState(0);
  const [knowledgeRevision, setKnowledgeRevision] = useState(0);
  const [characterLastName, setCharacterLastNameState] = useState<string | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const lastRefreshAtRef = useRef(0);
  const activeUserIdRef = useRef<string | null>(userId);

  const knowledgeLogs = useMemo(
    () => {
      void knowledgeRevision;
      return readKnowledgeHomeActivityLogs(userId);
    },
    [knowledgeRevision, userId]
  );
  const activeAgentReports = useMemo(
    () => agentReports.userId === userId ? agentReports : EMPTY_AGENT_REPORT_STATE,
    [agentReports, userId]
  );
  const agentTodayLogs = activeAgentReports.todayLogs;
  const agentYesterdayLogs = activeAgentReports.yesterdayLogs;
  const hasActiveJobs = activeAgentReports.hasActiveJobs;
  const todayLogs = useMemo(
    () => mergeHomeActivityLogsByRecency(agentTodayLogs, knowledgeLogs.today),
    [agentTodayLogs, knowledgeLogs.today]
  );
  const yesterdayLogs = useMemo(
    () => mergeHomeActivityLogsByRecency(agentYesterdayLogs, knowledgeLogs.yesterday),
    [agentYesterdayLogs, knowledgeLogs.yesterday]
  );
  const allLogs = useMemo(() => [...todayLogs, ...yesterdayLogs], [todayLogs, yesterdayLogs]);
  const unseenCount = useMemo(
    () => {
      void seenRevision;
      return getUnseenHomeReportFingerprints(userId, allLogs).length;
    },
    [allLogs, seenRevision, userId]
  );

  const refreshReports = useCallback(async ({ force = false }: RefreshOptions = {}) => {
    if (!enabled || !userId) return;
    if (inFlightRef.current) return inFlightRef.current;
    if (!force && Date.now() - lastRefreshAtRef.current < PASSIVE_REFRESH_MIN_INTERVAL_MS) return;

    const requestedUserId = userId;
    const request = (async () => {
      try {
        const response = await api.getAgentJobs({ limit: 50 });
        if (activeUserIdRef.current !== requestedUserId) return;
        const grouped = addPendingExecutiveMemoReport(
          groupAgentJobActivityLogsByDay(response.items ?? [], new Date()),
          new Date()
        );
        setAgentReports({
          userId: requestedUserId,
          todayLogs: grouped.todayLogs,
          yesterdayLogs: grouped.yesterdayLogs,
          hasActiveJobs: grouped.hasActiveJobs,
        });
        lastRefreshAtRef.current = Date.now();
      } catch (error) {
        logError(error, { action: "report_center_refresh" });
      }
    })().finally(() => {
      if (inFlightRef.current === request) inFlightRef.current = null;
    });
    inFlightRef.current = request;
    return request;
  }, [enabled, userId]);

  const closeReports = useCallback(() => setIsOpen(false), []);

  const openReports = useCallback(() => {
    setDrawerNewReportFingerprints(getUnseenHomeReportFingerprints(userId, allLogs));
    setIsOpen(true);
    void refreshReports({ force: true });
  }, [allLogs, refreshReports, userId]);

  const markViewedReports = useCallback((logs: HomeActivityLog[]) => {
    const viewedUnseen = getUnseenHomeReportFingerprints(userId, logs);
    if (viewedUnseen.length === 0) return;
    setDrawerNewReportFingerprints((current) => Array.from(new Set([...current, ...viewedUnseen])));
    markHomeReportsSeen(userId, logs);
    setSeenRevision((current) => current + 1);
  }, [userId]);

  const setCharacterLastName = useCallback((name: string | null | undefined) => {
    const normalized = name?.trim();
    if (normalized) setCharacterLastNameState(normalized);
  }, []);

  useEffect(() => {
    if (activeUserIdRef.current === userId) return;
    activeUserIdRef.current = userId;
    inFlightRef.current = null;
    lastRefreshAtRef.current = 0;
    setAgentReports(EMPTY_AGENT_REPORT_STATE);
    setDrawerNewReportFingerprints([]);
    setCharacterLastNameState(null);
    setIsOpen(false);
  }, [userId]);

  useEffect(() => {
    setIsOpen(false);
    if (enabled) void refreshReports({ force: true });
  }, [enabled, location.pathname, refreshReports]);

  useEffect(() => {
    if (!enabled) return;
    const handleFocus = () => void refreshReports();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void refreshReports();
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, refreshReports]);

  useEffect(() => {
    const handleKnowledgeActivity = () => setKnowledgeRevision((current) => current + 1);
    window.addEventListener(KNOWLEDGE_BACKGROUND_EVENT, handleKnowledgeActivity);
    return () => window.removeEventListener(KNOWLEDGE_BACKGROUND_EVENT, handleKnowledgeActivity);
  }, []);

  useEffect(() => {
    if (!enabled || !hasActiveJobs) return;
    const timer = window.setInterval(() => void refreshReports({ force: true }), ACTIVE_JOB_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, hasActiveJobs, refreshReports]);

  const value = useMemo<ReportCenterValue>(() => ({
    isEnabled: enabled,
    todayLogs,
    yesterdayLogs,
    unseenCount,
    hasActiveJobs,
    isOpen,
    openReports,
    closeReports,
    refreshReports,
    markViewedReports,
    setCharacterLastName,
  }), [enabled, todayLogs, yesterdayLogs, unseenCount, hasActiveJobs, isOpen, openReports, closeReports, refreshReports, markViewedReports, setCharacterLastName]);

  return (
    <ReportCenterContext.Provider value={value}>
      {children}
      {enabled && isOpen ? (
        <HomeReportDrawer
          characterLastName={characterLastName ?? "WizMate"}
          todayJobLogs={todayLogs}
          yesterdayJobLogs={yesterdayLogs}
          newReportFingerprints={drawerNewReportFingerprints}
          onLogsViewed={markViewedReports}
          onClose={closeReports}
          onNavigate={closeReports}
        />
      ) : null}
    </ReportCenterContext.Provider>
  );
}

export function useReportCenter(): ReportCenterValue {
  const value = useContext(ReportCenterContext);
  if (!value) throw new Error("useReportCenter must be used within ReportCenterProvider");
  return value;
}
